import API from 'utils/api';
import { onSuccess, onError } from 'utils/handlers';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { padronesEnum } from 'views/Tramites/GPM/enums';

export const baseURI = 'empresas/declaraciones-empresariales/';

/**
 * GET Declaraciones Empresariales
 * @param {Object} params
 * @returns {Promise<Array<object>>}
 */
export const getDeclaracionesEmpresariales = async (params = null) => {
  try {
    const response = await API.get(baseURI, { params });
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return { results: [] };
  }
};

/**
 * GET Declarciones Empresariales
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getDeclaracionEmpresarial = async (id) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Declaraciones empresariales
  * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const postDeclaracionEmpresarial = async (values) => {
  try {
    const response = await API.post(baseURI, getFormattedValues(values));
    onSuccess(response, 'Agregado correctamente');
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Declaraciones empresariales
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchDeclaracionEmpresarial = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}${id}`, getFormattedValues(values));
    onSuccess(response, 'Editado correctamente');
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};
/**
 * DELETE Declaraciones empresariales
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const deleteDeclaracionEmpresarial = async (id) => {
  try {
    const response = await API.delete(`${baseURI}${id}/`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};

const formatIndexed = (data) => formatReceived(data.map((i) => ({
  ...i,
  declaracion_empresarial: formatReceived(i.declaracion_empresarial),
})));

export const getDeclaracionesEmpresarialesNominas = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-nominas/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesArrendamiento = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-arrendamientos/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesRetenedoresDeNomina = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-retenciones-de-nomina/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesRetenedoresDeCedula = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-retenciones-cedulares/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesNotarios = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-notarios/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesHospedaje = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-hospedaje/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesVentaDeAlcoholes = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-bebidas-alcoholicas/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesJuegosDeAzar = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-apuestas/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesAgencia = async (params = null) => {
  try {
    const response = await API.get('recaudacion/declaraciones-empresariales-isan/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesCedularMensual = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-servicios/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

export const getDeclaracionesEmpresarialesCedularAnual = async (params = null) => {
  try {
    const response = await API.get('empresas/declaraciones-empresariales-de-servicio-anual/', { params });
    return formatIndexed(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

const enumCedularClases = {
  MENSUAL: 6,
  ANUAL: 14,
};

export const declaracionesGetters = {
  [padronesEnum.AGENCIA]: getDeclaracionesEmpresarialesAgencia,
  [padronesEnum.ARRENDAMIENTO]: getDeclaracionesEmpresarialesArrendamiento,
  [padronesEnum.CEDULAR]: {
    [enumCedularClases.MENSUAL]: getDeclaracionesEmpresarialesCedularMensual,
    [enumCedularClases.ANUAL]: getDeclaracionesEmpresarialesCedularAnual,
  },
  [padronesEnum.HOSPEDAJE]: getDeclaracionesEmpresarialesHospedaje,
  [padronesEnum.JUEGO_DE_AZAR]: getDeclaracionesEmpresarialesJuegosDeAzar,
  [padronesEnum.NOMINA]: getDeclaracionesEmpresarialesNominas,
  [padronesEnum.NOTARIO]: getDeclaracionesEmpresarialesNotarios,
  [padronesEnum.RETENEDOR_DE_CEDULA]: getDeclaracionesEmpresarialesRetenedoresDeCedula,
  [padronesEnum.RETENEDOR_DE_NOMINA]: getDeclaracionesEmpresarialesRetenedoresDeNomina,
  [padronesEnum.VENDEDOR_DE_ALCOHOLES]: getDeclaracionesEmpresarialesVentaDeAlcoholes,
};
